import { publicApiPath } from '@/lib/apiBase';
import { SIGNUP_SCREEN_PATH } from '@/lib/authSession';
import type { BillingMode } from '@/lib/checkoutPlans';
import { hasGuideShootingProgress } from '@/lib/shootingGuideResume';
import { getAuthToken, parseApiJson } from '@/lib/pwaSession';

export type SubscriptionState = 'active_unconsumed' | 'active_consumed' | 'no_subscription';

type GuidesPayload = {
  shootingGuide?: unknown | null;
  guides?: Array<{ status?: string }>;
  subscriptionState?: string;
  needsRepurchase?: boolean;
  credits?: { decouverte: number; video: number };
  needsPrestigeIntroChoice?: boolean;
};

export function isNewPurchaseFlow(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('new') === 'true';
}

/** Crédit vidéo ou forfait non consommé (hors envoi en cours). */
export function hasUnconsumedSubscription(
  state: string | undefined,
  credits?: GuidesPayload['credits'],
): boolean {
  if (state === 'active_unconsumed') return true;
  if (!credits) return false;
  return (credits.decouverte ?? 0) + (credits.video ?? 0) > 0;
}

export function hasActiveShootingGuide(data: GuidesPayload | null | undefined): boolean {
  if (!data) return false;
  if (data.shootingGuide) return true;
  return !!data.guides?.some((g) => g.status === 'in_progress');
}

type ShootingGuidePayload = {
  scenes?: Array<{ takes?: unknown[]; status?: string }>;
};

/** Évite ?new=true à la réouverture PWA quand un tournage est déjà commencé. */
export function shootingGuideEntryPath(data?: GuidesPayload | null): string {
  const guide = data?.shootingGuide as ShootingGuidePayload | null | undefined;
  if (guide?.scenes && hasGuideShootingProgress(guide.scenes)) {
    return '/shooting-guide';
  }
  if (hasActiveShootingGuide(data) && !guide) {
    return '/shooting-guide';
  }
  return '/shooting-guide?new=true';
}

/** Page d’accueil : tournage si forfait utilisable, galerie seulement si forfait déjà consommé. */
export function defaultAppPathForSubscription(
  state: string | undefined,
  guidesData?: GuidesPayload | null,
): string {
  if (state === 'no_subscription') return '/sign-up-login-screen?mode=signup';
  const credits = guidesData?.credits;
  if (hasUnconsumedSubscription(state, credits) || hasActiveShootingGuide(guidesData)) {
    return shootingGuideEntryPath(guidesData);
  }
  if (state === 'active_consumed' && !hasUnconsumedSubscription(state, credits)) return '/video-gallery';
  return shootingGuideEntryPath(guidesData);
}

/** @deprecated Utiliser defaultAppPathForSubscription */
export function postLoginRedirectPath(
  state: string | undefined,
  guidesData?: GuidesPayload | null,
): string {
  return defaultAppPathForSubscription(state, guidesData);
}

/** Résout la page d’accueil après connexion / ouverture PWA. */
export async function resolvePostLoginRedirect(
  token: string,
  subscriptionState: string | undefined,
): Promise<string> {
  if (subscriptionState === 'no_subscription') {
    return '/sign-up-login-screen?mode=signup';
  }
  if (subscriptionState === 'active_consumed') {
    try {
      const res = await fetch(publicApiPath('/v1/api/guides'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await parseApiJson<{ success?: boolean; data?: GuidesPayload }>(res);
      if (result?.success && result.data && hasUnconsumedSubscription(result.data.subscriptionState, result.data.credits)) {
        return shootingGuideEntryPath(result.data);
      }
    } catch {
      /* fallback galerie */
    }
    return '/video-gallery';
  }

  if (!getAuthToken()) {
    return SIGNUP_SCREEN_PATH;
  }

  try {
    const res = await fetch(publicApiPath('/v1/api/guides'), {
      headers: { Authorization: `Bearer ${token}` },
    });
    const result = await parseApiJson<{
      success?: boolean;
      data?: GuidesPayload;
    }>(res);
    if (res.status === 401 || (res.status === 403 && !result)) {
      return SIGNUP_SCREEN_PATH;
    }
    if (result?.success && result.data) {
      return defaultAppPathForSubscription(
        result.data.subscriptionState,
        result.data,
      );
    }
  } catch {
    /* fallback */
  }

  return shootingGuideEntryPath(null);
}

/** Choix du pack — uniquement pour un nouveau film après forfait consommé. */
export const REPURCHASE_PLAN_PATH = '/sign-up-login-screen?repurchase=1';

/** @deprecated */
export const NEW_GUIDE_PATH = REPURCHASE_PLAN_PATH;

export function guideNavHrefFromGuidesApi(data: GuidesPayload | null | undefined): string {
  const state = data?.subscriptionState;
  if (hasUnconsumedSubscription(state, data?.credits) || hasActiveShootingGuide(data)) {
    return shootingGuideEntryPath(data);
  }
  if (state === 'active_consumed' || data?.needsRepurchase) return REPURCHASE_PLAN_PATH;
  if (state === 'no_subscription') return '/sign-up-login-screen?mode=signup';
  return '/shooting-guide';
}

export function buildPlansRepurchaseUrl(): string {
  return REPURCHASE_PLAN_PATH;
}

export function buildRepurchasePaymentUrl(planId: string, billingMode: BillingMode = 'pack_5'): string {
  const params = new URLSearchParams({ repurchase: '1', plan: planId, billingMode });
  return `/payment-screen?${params.toString()}`;
}
